export interface User {
	uid: string,
	email: string,
	displayName: string,
	emailVerified: boolean,

	supportLvl?: number,
	noPassword?: boolean,
}
export interface UserDoc {
	uid: string,
	email: string,
	lastVisitedAccount?: string | null,
	lastLoginTime?: number | null,
	visitedAccounts?: string[],
}

export interface ProfileSubmission {
	displayName?: string,
	photoURL?: string,
}
export interface LoginSubmission {
	email: string,
	password: string,
}
export interface TypicalResponse {
	success: boolean,
	errorCode?: string,
	errorMessage?: string,
	error?: any,
}
export interface SetSupportLvlResponse extends TypicalResponse {
	email: string,
	supportLvl: number,
}
export interface StateKeyPairs {
	key: string,
	state: ProcessState
}

export enum ProcessState {
	Undefined,
	Command,
	Preprocess,
	Attempt,
	Await,
	Succeed,
	Failed,
}

export type LoginStatus = 'loading' | 'logged in' | 'logged out';
export type EditState = 'active' | 'sending' | 'done';

export interface ToastData {
	id: string,
	title: string,
	state: string,
	destroyWhenHidden: boolean,
	variant?: string,
}

export const softwareTypes = ['sql', 'autocount'] as const;
export type SoftwareType = typeof softwareTypes[number];

export interface AccountData extends AccountMinimumData {
	accountId: string,
	companyName: string,
	companyRemark: string,
	softwareType: string,
	interval: number,
	schemaId: string,
	programId: string,
	gdbid: string,
	coldDb: string,
	path: string,
	payroll?: string,
	token?: MwToken,
	oldTokens: MwTokenDic,
	backendUrl?: string,
	createdBy: string,
	createdAt: number,
	lastSync: number,
	lastUpdate: number,
	mwVersion?: string,
	dashboardLastAvailable?: GdbPathMinimum,
	globalLastAvailable: GdbPathMinimum,
	salesLastAvailable: GdbPathMinimum,
	quotationsLastAvailable: GdbPathMinimum,
	purchasesLastAvailable: GdbPathMinimum,
	transactionsLastAvailable: GdbPathMinimum,
	suppliersLastAvailable?: GdbPathMinimum,
	customersLastAvailable?: GdbPathMinimum,
	payrollLastAvailable?: GdbPathMinimum,
	stocksLastAvailable?: GdbPathMinimum,
}
export interface GdbPathMinimum {
	gdbid: string,
	version: number,
	uploadedAt?: number,
	downloadUrl?: string,
}
export interface CsvEntry {
	bucketUrl: string;
	downloadUrl: string;
	size: number;
	parquetUrl?: string;
	parquetBucketUrl?: string;
}
export type DownloadFormat = 'both' | 'csv' | 'parquet';
export type FileFormat = 'csv' | 'parquet' | 'both';
export const fileFormats: FileFormat[] = ['both', 'csv', 'parquet'];

export interface GdbPath extends GdbPathMinimum {
	bucketUrl?: string,
	accountId: string,
	createdAt: number,
	csvs?: { [key: string]: CsvEntry },
}
export interface AccountMinimumData {
	accountId: string,
	companyName: string,
	companyRemark: string,
	createdBy: string,
	createdAt: number,
	expiryTime: number,
}
export interface AccountMinimumDataDic {
	[key: string]: AccountMinimumData | null;
}

export interface MwToken {
	id?: string,
	key: string,
	version: number,
	createdAt?: number,
	createdBy?: string,
	forbidden?: boolean,
}

export interface CommandData {
	commandId: string,
	accountId: string,
	type: string,
	status: string,
	issuedAt: number,
	hasError: boolean,
	payload?: any,
	logs?: CommandLog[],
	inactive?: boolean,
	completedAt?: number,
	mwVersion?: string,
	mwDomain?: string,
}
export interface CommandLog {
	time?: number,
	level: 'debug' | 'info' | 'error' | 'mw_error',
	message: string,
}

export interface Installers {
	x86: string | null,
	x64: string | null,
	version: string | null,
}

export interface CreateAccountSubmission {
	companyName: string,
	companyRemark: string,
	path: string,
	payroll?: string,
	programId?: string,
	interval?: number,
	softwareType?: SoftwareType,
}

export interface CreateAccountResponse extends TypicalResponse {
	accountData?: AccountData;
}

export interface AccountRelatedSubmission {
	accountId: string,
}
export interface UpdateAccountRemarkSubmission extends AccountRelatedSubmission {
	companyRemark: string,
}
export interface UpdateAccountExprirySubmission extends AccountRelatedSubmission {
	expiryTime: number,
}
export interface UpdateAccountSubmission extends AccountRelatedSubmission {
	path?: string,
	payroll?: string,
	interval?: number,
	backendUrl?: string,
	'token.forbidden'?: boolean,
}
export interface BanTokenSubmission extends AccountRelatedSubmission {
	tokenPath: string,
	value: boolean,
}
export interface ChangeSchemaSubmission extends AccountRelatedSubmission {
	schemaId: string,
}
export interface ChangeProgramSubmission extends AccountRelatedSubmission {
	programId: string,
}
export interface CommandReponse extends TypicalResponse {
	command?: CommandData,
}
export interface ExpireCurrentTokenSubmission extends AccountRelatedSubmission {
	doNotMakeNew?: boolean,
}
export interface ExpireCurrentTokenResponse extends TypicalResponse {
	token?: MwToken,
	oldTokens?: MwTokenDic,
	companyName?: string,
	accountId?: string,
}

export interface MwTokenDic {
	[key: string]: MwToken;
}

export interface ValueText {
	value: any,
	text: string,
}

export interface SchemaData {
	id: string,
	createdAt?: number,
	parentId: string | null,
	softwareType: SoftwareType,
	model: string,
	solution: string,
	parsedModel?: TableSchema[],
	parsedSolution?: TableSchema[],
}

export interface TableSchema {
	id: string,
	table: string,
	collection: string,
	keyField: string,
	type: 'master' | 'detail' | 'remove' | 'inherit',
	extractor: ExtractorSchema,
	masterKey?: string,
	detailPath?: string,
}
export type ExtratorConvertPair = [string | boolean | number | null, string | boolean | number | null, string];
export type ExtractorItem = [string, 'JOIN' | 'EQUAL' | 'CONVERT' | 'DATE', string | ExtractorSchema,
	ExtratorConvertPair[]?];

export interface ExtractorSchema { [key: string]: ExtractorItem }

export type SchemaDic = {
	[key in SoftwareType]: SchemaData[];
};

export interface SetPermmissionSubmission extends AccountRelatedSubmission {
	email: string,
	admin?: boolean,
	dashboard?: boolean,
	sales?: boolean,
	purchases?: boolean,
	quotations?: boolean,
	transactions?: boolean,
	suppliers?: boolean,
	customers?: boolean,
	payroll?: boolean,
	stocks?: boolean,
	dump?: boolean,
	forecastRead?: boolean,
	forecastWrite?: boolean,
}

export interface AddRemoveAccountUserSubmission extends AccountRelatedSubmission {
	email: string,
	remove?: boolean,
}

export interface PermissionsData {
	[email: string]: AccountUserPermission,
}

export interface AccountUserPermission extends SetPermmissionSubmission {
	uid: string | null,
	displayName?: string,
}
export interface AddProgramSubmission {
	id: string,
	softwareType: SoftwareType,
}

export interface UpdateProgramSubmission {
	id: string,
	groups: Program[],
	fileFormat?: FileFormat,
	version?: any,
	updatedAt?: number,
}
export interface ProgramData {
	groups: Program[],
	version: number,
	updatedAt: number,
	createdAt: number,
	softwareType: SoftwareType,
	id: string,
	fileFormat?: FileFormat,
}
export interface Program {
	id?: string | number,
	group?: string,
	target?: string,
	queries: Query[],
}
export interface Query {
	id?: string | number,
	file: string,
	query: string,
}
export const permissionsGroups = ['dashboard', 'global', 'sales', 'quotations',
	'purchases', 'transactions', 'suppliers', 'customers', 'payroll', 'stocks', 'dump'] as const;
export type PermissionsGroup = typeof permissionsGroups[number];
